import { useMutation, useQuery } from '@apollo/client';
import { ArrowRight, Download, LinkedIn, Share } from '@mui/icons-material';
import { gql } from '__generated__';
import * as htmlToImage from 'html-to-image';
import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import SidebarLayout, {
  SidebarLayoutContent,
  SidebarLayoutNavigation,
} from 'layouts/SidebarLayout';

import ErrorMessage from 'components/ErrorMessage';

import Button from 'primitives/Button';
import LoadingIndicator from 'primitives/LoadingIndicator';

import ImageCarousel from './ImageCarousel';
import WrappedCollage from './WrappedCollage';

const WRAPPED_QUERY = gql(`
    query GetWrapped {
        wrapped2025 {
            capitalDeployed
            numberOfDeals
            numberOfLps
            top3Industries
            personality
            syndicateName
            isEligible
            postPublished
        }
    }
    `);

const WRAPPED_UPLOAD_MUTATION = gql(`
    mutation WrappedUpload($image: Upload!) {
      createWrappedFile(image: $image) {
            imageUrl
        }
    }
    `);

const Wrapped2025Page: FC = () => {
  const { data, loading, error, refetch } = useQuery(WRAPPED_QUERY);
  const [wrappedImageUrl, setWrappedImageUrl] = useState<string | null>(null);
  const [createWrappedFile, { loading: uploadLoading, error: uploadError }] =
    useMutation(WRAPPED_UPLOAD_MUTATION);
  const [simulateDownload, setSimulateDownload] = useState(false);
  const navigate = useNavigate();

  async function autoUploadImage() {
    try {
      const wrappedImageUrl = localStorage.getItem('wrappedImageUrl');
      if (wrappedImageUrl) {
        setWrappedImageUrl(wrappedImageUrl);
        return;
      }
      setSimulateDownload(true);
      const node = document.getElementById('wrapped-collage');
      if (!node) {
        console.error('Element not found');
        setSimulateDownload(false);
        return;
      }

      const dataUrl = await htmlToImage.toJpeg(node, { quality: 0.95 });

      const res = await fetch(dataUrl);
      const blob = await res.blob();

      const image = new File([blob], 'wrapped-2025.jpeg', {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });
      setSimulateDownload(false);

      createWrappedFile({ variables: { image } })
        .then(response => {
          console.log(response);
          setWrappedImageUrl(response?.data?.createWrappedFile?.imageUrl || null);
          if (response?.data?.createWrappedFile?.imageUrl) {
            localStorage.setItem('wrappedImageUrl', response?.data?.createWrappedFile?.imageUrl);
          }
        })
        .catch(error => {
          console.error(error);
        });
    } catch (error) {
      console.error(error);
      setSimulateDownload(false);
    }
  }

  function shareOnTwitter() {
    const twitterUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(
      wrappedImageUrl || ''
    )}&text=${encodeURIComponent('Check out my year in venture, powered by AngelList India!')}`;
    window.open(twitterUrl, '_blank');
  }

  function shareOnLinkedIn() {
    const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
      wrappedImageUrl || ''
    )}`;
    window.open(linkedinUrl, '_blank');
  }

  useEffect(() => {
    autoUploadImage();
  }, [loading]);

  function downloadImage() {
    try {
      setSimulateDownload(true);

      // Wait for React to render the element to the DOM
      setTimeout(() => {
        const node = document.getElementById('wrapped-collage');
        if (!node) {
          console.error('Element not found');
          setSimulateDownload(false);
          return;
        }

        htmlToImage
          .toJpeg(node, { quality: 0.95 })
          .then(function (dataUrl) {
            var link = document.createElement('a');
            link.download = 'angellist-india-wrapped-2025.jpeg';
            link.href = dataUrl;
            link.click();
            // Reset state only after successful download
            setSimulateDownload(false);
          })
          .catch(function (error) {
            console.error('Error generating image:', error);
            setSimulateDownload(false);
          });
      }, 10); // Small delay to ensure DOM is ready
    } catch (error) {
      console.error(error);
      setSimulateDownload(false);
    }
  }

  const wrappedData = [
    {
      title: 'Number of Deals',
      value: data?.wrapped2025.numberOfDeals,
      format: 'NUMBER',
    },
    {
      title: 'Capital Deployed',
      value: data?.wrapped2025.capitalDeployed,
      format: 'CURRENCY',
    },
    {
      title: 'Number of LPs',
      value: data?.wrapped2025.numberOfLps,
      format: 'NUMBER',
    },
    ...(!!data?.wrapped2025?.postPublished
      ? [
          {
            title: 'LP Updates  Published',
            value: data?.wrapped2025.postPublished,
            format: 'NUMBER',
          },
        ]
      : []),
    {
      title: 'Top Industries',
      value: data?.wrapped2025.top3Industries?.map(industry =>
        industry
          ?.toLowerCase()
          .replace(/_/g, ' ')
          .replace(/\b\w/g, c => c.toUpperCase())
      ),
      format: 'ARRAY',
    },
    {
      title: 'Wrapped Peronality',
      value: data?.wrapped2025.personality,
      format: 'SMALL_STRING',
    },
  ];

  const renderContent = () => {
    if (loading) return <LoadingIndicator />;
    if (error) return <ErrorMessage error={error} refetch={refetch} />;

    if (!data?.wrapped2025.isEligible) {
      navigate(`/invest`);
      return null;
    }

    return (
      <>
        <SidebarLayoutNavigation
          title="Wrapped 2025"
          subTitle="Your wrapped 2025 is ready to share"
        />
        <SidebarLayoutContent>
          <>
            <div className="relative min-h-[calc(100vh-200px)] overflow-hidden">
              <div className="relative z-10 flex flex-col lg:flex-row items-center justify-center gap-8 p-8">
                <div className="flex-1 flex items-center justify-center transition-all duration-1000 opacity-100 translate-y-0 scale-100">
                  <div className="relative group">
                    <div className="relative bg-white rounded-2xl p-1 shadow-2xl transform transition-transform duration-300 group-hover:scale-[1.02]">
                      <ImageCarousel
                        syndicateName={data?.wrapped2025.syndicateName}
                        wrappedData={wrappedData}
                      />
                    </div>
                  </div>
                </div>
                <div className="flex-1 flex flex-col items-center justify-center gap-8 transition-all duration-1000 delay-300 opacity-100 translate-x-0">
                  {/* Celebratory header */}
                  <div className="text-center space-y-3">
                    <h2 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-indigo-600 via-blue-600 to-purple-600 bg-clip-text text-transparent">
                      Your Year in Review
                    </h2>
                    <p className="text-lg text-gray-600 max-w-md">
                      Celebrate your investment journey and share your achievements with the world
                    </p>
                  </div>
                  <div className="w-full max-w-md space-y-4">
                    {/* Download button - primary action */}
                    <Button
                      className="w-full py-4 text-base font-semibold bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl transform transition-all duration-300 hover:scale-105"
                      variant="custom"
                      onClick={() => {
                        downloadImage();
                      }}
                    >
                      <Download className="mr-2" />
                      Download Image
                    </Button>

                    {/* Social share buttons */}
                    <div className="grid grid-cols-2 gap-4">
                      <Button
                        className="w-full py-3 text-sm font-semibold bg-white hover:bg-blue-50 text-blue-600 border-2 border-blue-200 hover:border-blue-400 shadow-md hover:shadow-lg transform transition-all duration-300 hover:scale-105"
                        disabled={!wrappedImageUrl}
                        disabledMessage="Please wait for the image to be uploaded"
                        variant="custom"
                        onClick={() => {
                          shareOnLinkedIn();
                        }}
                      >
                        <LinkedIn className="mr-2" />
                        LinkedIn
                      </Button>

                      <Button
                        className="w-full py-3 text-sm font-semibold bg-white hover:bg-gray-50 text-gray-900 border-2 border-gray-200 hover:border-gray-400 shadow-md hover:shadow-lg transform transition-all duration-300 hover:scale-105"
                        disabled={!wrappedImageUrl}
                        disabledMessage="Please wait for the image to be uploaded"
                        variant="custom"
                        onClick={() => {
                          shareOnTwitter();
                        }}
                      >
                        <span className="font-bold mr-2 text-lg">𝕏</span>X
                      </Button>
                    </div>
                  </div>

                  <div className="mt-4 text-center">
                    <p className="text-sm text-gray-500 italic flex items-center justify-center gap-2">
                      Need a personalized Wrapped experience?
                      <a href="mailto:hi@angellistindia.com" className="text-blue-500">
                        Contact us
                      </a>
                      <ArrowRight className="w-4 h-4 -ml-2" />
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </>
          <WrappedCollage
            wrappedData={wrappedData}
            syndicateName={data?.wrapped2025.syndicateName}
            className={simulateDownload ? '' : 'fixed -left-[9999px] -top-[9999px] invisible'}
          />
        </SidebarLayoutContent>
      </>
    );
  };

  return <SidebarLayout>{renderContent()}</SidebarLayout>;
};

export default Wrapped2025Page;
